import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Stack, Avatar, Chip, TextField, MenuItem, CircularProgress } from '@mui/material';
import SearchIcon         from '@mui/icons-material/Search';
import MoreTimeIcon       from '@mui/icons-material/MoreTime';
import * as XLSX          from 'xlsx';
import jsPDF              from 'jspdf';
import autoTable          from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import apiService         from '../../services/apiServices';
import PageHeader         from '../common/PageHeader';
import OvertimeBankPanel  from './OvertimeBankPanel';
import { ReportPage, ExportMenu } from './ReportKit';
import { activeWorkers } from '../../utils/workerStatus';

const todayStr   = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function fmtHHMM(min) {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
}
function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

const STANDARD_MIN = 480; // 8h

export default function OvertimeReport() {
  const { t } = useTranslation();
  const [dateFrom,    setDateFrom]    = useState(monthStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [fWorker,     setFWorker]     = useState('');
  const [fDept,       setFDept]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [entries,     setEntries]     = useState([]);
  const [workers,     setWorkers]     = useState([]);
  const [departments, setDepartments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, dRes, eRes] = await Promise.all([
        apiService().get('workers'),
        apiService().get('departments'),
        apiService().get('timeEntries'),
      ]);
      if (wRes.success) setWorkers(wRes.data || []);
      if (dRes.success) setDepartments(dRes.data || []);
      if (eRes.success) setEntries(eRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const workerById = useCallback(id => workers.find(w => String(w.Id || w.id) === String(id)), [workers]);
  const wName      = useCallback(id => { const w = workerById(id); return w ? `${w.FirstName || ''} ${w.LastName || ''}`.trim() : `#${id}`; }, [workerById]);
  const wDeptId    = useCallback(id => { const w = workerById(id); return w ? String(w.Department || w.department_id || '') : ''; }, [workerById]);
  const wDeptName  = useCallback(id => { const w = workerById(id); return w ? (w.DepartmentName || '') : ''; }, [workerById]);

  // Lista rënëse është ZGJEDHJE, jo të dhëna: i larguari s'ka pse të zgjidhet.
  // Harta id→emër më lart mbetet e plotë, që rreshtat e periudhës kur ai ishte
  // ende në punë të mos dalin pa emër.
  const deptWorkers = useMemo(() =>
    activeWorkers(fDept ? workers.filter(w => String(w.Department || w.department_id || '') === String(fDept)) : workers)
  , [workers, fDept]);

  // group entries by worker+date in range
  const rows = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      if (!e.date || !e.worker_id) return;
      if (e.date < dateFrom || e.date > dateTo) return;
      if (fWorker && String(e.worker_id) !== String(fWorker)) return;
      if (fDept   && wDeptId(e.worker_id) !== String(fDept)) return;
      const key = `${e.worker_id}__${e.date}`;
      if (!map[key]) map[key] = { wid: String(e.worker_id), date: e.date, totalMin: 0 };
      map[key].totalMin += (e.duration_seconds || 0) / 60;
    });
    return Object.values(map)
      .filter(r => r.totalMin > STANDARD_MIN)
      .map(r => ({
        ...r,
        name:      wName(r.wid),
        deptName:  wDeptName(r.wid),
        stdMin:    STANDARD_MIN,
        extraMin:  Math.max(0, r.totalMin - STANDARD_MIN),
      }))
      .sort((a, b) => b.extraMin - a.extraMin || b.date.localeCompare(a.date));
  }, [entries, dateFrom, dateTo, fWorker, fDept, wName, wDeptName, wDeptId]);

  const stats = useMemo(() => ({
    total: rows.length,
    totalExtra: rows.reduce((s, r) => s + r.extraMin, 0),
    avgExtra:   rows.length > 0 ? rows.reduce((s, r) => s + r.extraMin, 0) / rows.length : 0,
    topWorker:  rows.length > 0 ? rows.reduce((a, b) => a.extraMin > b.extraMin ? a : b).name : '—',
  }), [rows]);

  const HEADERS = [t('report.worker'), t('report.department'), t('report.date'), t('report.standardHours'), t('report.workedHours'), t('report.extraHours')];
  const expRows  = () => rows.map(r => [r.name, r.deptName, r.date, fmtHHMM(r.stdMin), fmtHHMM(r.totalMin), fmtHHMM(r.extraMin)]);

  const exportCSV = () => {
    const lines = [HEADERS, ...expRows()].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','));
    const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(lines.join('\n'));
    a.download = `overtime-${todayStr()}.csv`; a.click();
  };
  const exportExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...expRows()]);
    ws['!cols'] = [24, 20, 12, 14, 14, 14].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, t('report.extraHours'));
    XLSX.writeFile(wb, `overtime-${todayStr()}.xlsx`);
  };
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text('Raporti i Orëve Shtesë', 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(`Periudha: ${dateFrom} — ${dateTo}`, 14, 22);
    autoTable(doc, { head: [HEADERS], body: expRows(), startY: 27, styles: { fontSize: 8 }, headStyles: { fillColor: [234, 88, 12] } });
    doc.save(`overtime-${todayStr()}.pdf`);
  };

  return (
    <ReportPage>
      <PageHeader title={t('reports.overtime')} subtitle={t('reports.overtimeDesc')} />

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700}>{t('report.filters')}</Typography>
          {(fWorker || fDept) && <Chip size="small" label={t('report.clear')} onClick={() => { setFWorker(''); setFDept(''); }} onDelete={() => { setFWorker(''); setFDept(''); }} />}
          <Chip size="small" label={t('report.refresh')} onClick={load} variant="outlined" sx={{ ml: 'auto' }} />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}><TextField type="date" size="small" fullWidth label={t('report.from')} InputLabelProps={{ shrink: true }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3}><TextField type="date" size="small" fullWidth label={t('report.to')} InputLabelProps={{ shrink: true }} value={dateTo} onChange={e => setDateTo(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3}>
            <TextField select size="small" fullWidth label={t('report.department')} value={fDept} onChange={e => { setFDept(e.target.value); setFWorker(''); }}>
              <MenuItem value=""><em>{t('report.allDepartments')}</em></MenuItem>
              {departments.map(d => <MenuItem key={d.id || d.Id} value={d.id || d.Id}>{d.name || d.Name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select size="small" fullWidth label={t('report.worker')} value={fWorker} onChange={e => setFWorker(e.target.value)}>
              <MenuItem value=""><em>{t('report.allWorkers')}</em></MenuItem>
              {deptWorkers.map(w => <MenuItem key={w.Id || w.id} value={w.Id || w.id}>{w.FirstName || ''} {w.LastName || ''}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('report.daysWithOvertime'), val: stats.total, color: 'warning.main' },
          { label: t('report.totalOvertimeHours'),  val: fmtHHMM(stats.totalExtra), color: 'error.main' },
          { label: t('report.avgOvertimePerDay'), val: fmtHHMM(stats.avgExtra), color: 'primary.main' },
          { label: t('report.topOvertimeWorker'), val: stats.topWorker, color: 'secondary.main' },
        ].map(c => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={800} color={c.color} noWrap sx={{ fontSize: rows.length && c.label === t('report.topOvertimeWorker') ? '1rem' : undefined }}>{c.val}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MoreTimeIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>{t('report.overtimeByDay')}</Typography>
            <Chip size="small" label={`${rows.length}`} sx={{ fontSize: '0.72rem' }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <ExportMenu onCsv={exportCSV} onExcel={exportExcel} onPdf={exportPDF} />
          </Stack>
        </Stack>
        <Box sx={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {HEADERS.map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={HEADERS.length} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={HEADERS.length} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('report.noOvertimeData')}</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={r.wid + r.date} hover sx={{ bgcolor: i % 2 ? '#FAFAFA' : 'white' }}>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: 'warning.main' }}>{initials(r.name)}</Avatar>
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{r.deptName || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.date}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'text.secondary' }}>{fmtHHMM(r.stdMin)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}>{fmtHHMM(r.totalMin)}</TableCell>
                  <TableCell>
                    <Chip size="small" color="warning" label={fmtHHMM(r.extraMin)} sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.78rem' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {rows.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Stack direction="row" spacing={3}>
              <Typography variant="caption" color="text.secondary">{t('report.totalOvertimeLabel')} <strong>{fmtHHMM(stats.totalExtra)}</strong></Typography>
              <Typography variant="caption" color="text.secondary">{t('report.avgLabel')} <strong>{fmtHHMM(stats.avgExtra)}</strong></Typography>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* OT bank panel — shown when a specific worker is filtered */}
      {fWorker && (() => {
        const w = workerById(fWorker);
        const wName = w ? `${w.FirstName || ''} ${w.LastName || ''}`.trim() : '';
        return (
          <OvertimeBankPanel
            workerId={fWorker}
            workerName={wName}
            year={dateFrom ? new Date(dateFrom).getFullYear() : undefined}
          />
        );
      })()}
    </ReportPage>
  );
}
